<template>
  <v-container fluid class="sci-page-wrapper pa-0" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        
        <!-- HEADER -->
      <div class="sci-page-header pb-4">
          <h1 class="sci-page-title-only">Fornecedores | Almoxarifado</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">Gestão e controle de fornecedores de almoxarifado cadastrados no sistema</p>
        </div>

        <!-- LISTAGEM -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <!-- BARRA DE FERRAMENTAS DA TABELA -->
            <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
                <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">RELAÇÃO DE FORNECEDORES</h2>
                </div>
                
                <div class="flex items-center gap-4 flex-1 justify-end">
                  <div class="flex-none">
                    <button 
                      @click="$router.push('/parts-and-services/providers-manager/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-sm uppercase">NOVO</span>
                    </button>
                  </div>
                  
                  <div class="hidden md:flex relative w-80">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                    </div>
                    <input 
                      v-model="search"
                      placeholder="PESQUISAR FORNECEDOR..."
                      class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
                </div>
            </div>

            <!-- SEARCH MOBILE -->
            <div class="md:hidden p-4 bg-slate-50 border-b border-slate-100">
               <v-text-field
                v-model="search"
                placeholder="PESQUISAR..."
                solo flat dense hide-details
                prepend-inner-icon="mdi-magnify"
                background-color="white"
                class="custom-select"
              />
            </div>

            <div class="p-0">
                <v-data-table
                  :headers="headers"
                  :items="data"
                  :search="search"
                  :page.sync="page"
                  :items-per-page="itemsPerPage"
                  @page-count="pageCount = $event"
                  :loading="loader"
                  loading-text="CARREGANDO DADOS..."
                  class="custom-table"
                  hide-default-footer
                >
                  <template v-slot:item.provider_name="{ item }">
                    <div class="flex items-center gap-3 py-2">
                      <v-avatar size="36" class="border border-slate-100 bg-slate-50">
                        <img :src="item.provider_logo ? 'https://api.sigfrotagov.com.br/uploads/' + item.provider_logo?.replace('https://api.sigfrotagov.com.br/uploads/', '') : require('@/assets/no-image.jpg')" alt="Logo" class="object-cover">
                      </v-avatar>
                      <div class="flex flex-col">
                        <span class="text-sm font-bold text-slate-800 block tracking-tight">{{ item.provider_name }}</span>
                        <span class="text-[10px] text-slate-400 font-bold uppercase">{{ item.provider_document }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.provider_mail="{ item }">
                    <span class="text-xs text-slate-600 font-medium lowercase">{{ item.provider_mail }}</span>
                  </template>

                  <template v-slot:item.provider_tel="{ item }">
                    <span class="text-xs text-slate-500 font-semibold">{{ item.provider_tel || item.provider_cel }}</span>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <div class="flex justify-end gap-2">
                      <button 
                        @click="$router.push(`/parts-and-services/providers-manager/edit/${item.provider_id}`)"
                        class="p-2 hover:bg-orange-50 text-slate-400 hover:text-orange-500 rounded-lg transition-all"
                        title="Editar"
                      >
                        <v-icon small color="currentColor">mdi-pencil-outline</v-icon>
                      </button>
                      <button 
                        @click="deleteItem(item.provider_id)"
                        class="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-all"
                        title="Excluir"
                      >
                        <v-icon small color="currentColor">mdi-trash-can-outline</v-icon>
                      </button>
                    </div>
                  </template>
                </v-data-table>

                <!-- Custom Pagination Footer -->
                <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center w-full">
                    <div class="flex-1 text-left">
                      <span class="text-xs text-slate-500 font-medium whitespace-nowrap">Exibindo {{ paginationStart }} de {{ data.length }} registros</span>
                    </div>
                    
                    <div class="flex-1 flex justify-end gap-1">
                        <button 
                          @click="page > 1 ? page-- : null" 
                          :disabled="page === 1"
                          class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs text-slate-500 font-medium"
                        >
                          Anterior
                        </button>
                        
                        <button 
                          v-for="p in visiblePages" 
                          :key="p"
                          @click="page = p"
                          :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                          class="px-3 py-1.5 border rounded-md transition-colors text-xs font-medium"
                        >
                          {{ p }}
                        </button>

                        <button 
                          @click="page < pageCount ? page++ : null" 
                          :disabled="page === pageCount"
                          class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs text-slate-500 font-medium"
                        >
                          Próximo
                        </button>
                    </div>
                </div>
            </div>
        </section>
      </div>
    </div>

    <v-snackbar multi-line border="left" :color="snack.color" v-model="snack.show" transition="scroll-y-reverse-transition" class="mb-6">
      <div class="flex items-center gap-3">
        <v-icon color="white">mdi-information-outline</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Fornecedor", value: "provider_name", align: 'start', sortable: true },
        { text: "E-mail", value: "provider_mail", align: 'start' },
        { text: "Telefone", value: "provider_tel", align: 'start' },
        { text: "Ações", value: "action", align: "right", sortable: false },
      ],
    };
  },
  computed: {
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get("/parts-and-services/providers")
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      if (confirm("Deseja excluir o registro?")) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/parts-and-services/providers/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-table ::v-deep table thead th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  height: 48px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.custom-table ::v-deep table tbody td {
  height: 64px !important;
  color: #334155 !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.custom-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.custom-select ::v-deep .v-input__slot {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  height: 44px !important;
}

.custom-select ::v-deep input {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  text-transform: uppercase !important;
}
</style>
